import {omit} from 'lodash';
import React, {FC, useState} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {
  AsyncHandler,
  Handler,
  HandlerOf,
  RendererOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {isTeamAdmin} from '../../../../helpers/models/accessHelpers';
import {isTeamNamespace} from '../../../../helpers/models/namespaceHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {getAccess} from '../../../../store/data/dataSelectors';
import {deleteLensAsyncAction} from '../../../../store/data/lenses/saveLensActions';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {DropdownSeparator} from '../../../common/dropdowns/dropdownSeparator';
import {PermissionSimpleDropdownItem} from '../../../common/dropdowns/permissionSimpleDropdownItem';
import {useLinkNavigation} from '../../../common/navigation/useLinkNavigation';
import {EditSidebarCopy} from '../editor/copy/edit-sidebar-copy';
import {HideInSidebarCopy} from '../editor/copy/hide-in-sidebar-copy';
import {DeleteLensDialog} from './dialog/deleteLensDialog';
import {LensesWorkspaceEditDialog} from './dialog/lensesWorkspaceEditDialog';
import {LensesWorkspaceItem, LensesWorkspaceItemProps} from './lensesWorkspaceItem';

/*
 * Props.
 */

export interface LensesWorkspaceItemContainerComponentProps extends LensesWorkspaceItemProps {
  panelId: InboxesPanelId;
  onEditWorkspaces: Handler;
  onHideFromWorkspace: HandlerOf<number>;
  shouldHideMoreMenu: boolean;
  shouldShowRefinedResultsIndicator: boolean;
}

/*
 * Component.
 */

export const LensesWorkspaceItemContainerComponent: FC<LensesWorkspaceItemContainerComponentProps> = (
  props,
) => {
  const {lens, panelId} = props;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const access = useAppSelector(getAccess);

  const dispatch = useAppDispatch();
  const deleteLensAsync = () =>
    dispatch(deleteLensAsyncAction({inboxesPanelId: props.panelId, lensId: lens.id, lensName: lens.name}));
  const onDelete: AsyncHandler = async () => {
    await deleteLensAsync();
  };
  const {navigateToLink} = useLinkNavigation();

  const componentProps = omit(props, ['onEditWorkspaces', 'shouldHideMoreMenu', 'onHideFromWorkspace']);
  const onHide: Handler = () => {
    props.onHideFromWorkspace(lens.id);
  };

  const onEditClick: Handler = () => {
    if (isTeamNamespace(lens.namespace)) {
      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.LENSES,
          lensId: lens.id,
          namespace: lens.namespace,
          resourcePage: SettingsResourcePagesEnum.EDIT,
        }),
      );
    } else {
      setIsEditDialogOpen(true);
    }
  };

  const isReadOnly = isTeamNamespace(lens.namespace) && !isTeamAdmin(lens.namespace, access);

  const renderMoreMenu = createRenderMoreMenu(props, isReadOnly, onHide, onEditClick);
  return (
    <>
      <LensesWorkspaceItem {...componentProps} renderMenu={renderMoreMenu} />
      {isEditDialogOpen && (
        <LensesWorkspaceEditDialog
          panelId={panelId}
          lens={lens}
          onRequestClose={() => setIsEditDialogOpen(false)}
          onDelete={() => setIsDeleteDialogOpen(true)}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteLensDialog
          lens={lens}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setIsEditDialogOpen(true);
          }}
          onDelete={onDelete}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function createRenderMoreMenu(
  props: LensesWorkspaceItemContainerComponentProps,
  isReadOnly: boolean,
  onHide: Handler,
  onEditClick: Handler,
): RendererOf<PopoverTransferProps> | undefined {
  const {shouldHideMoreMenu} = props;
  if (shouldHideMoreMenu) {
    return undefined;
  }

  return (popoverProps) => renderMoreDropdown(popoverProps, props, isReadOnly, onHide, onEditClick);
}

function renderMoreDropdown(
  popoverProps: PopoverTransferProps,
  props: LensesWorkspaceItemContainerComponentProps,
  isReadOnly: boolean,
  onHide: Handler,
  onEditClick: Handler,
) {
  return (
    <SimpleDropdown {...popoverProps}>
      {!isReadOnly && (
        <PermissionSimpleDropdownItem onSelect={onEditClick}>
          <Trans id="lenses-workspace-edit-lens">Edit view</Trans>
        </PermissionSimpleDropdownItem>
      )}
      <PermissionSimpleDropdownItem onSelect={onHide}>
        <HideInSidebarCopy />
      </PermissionSimpleDropdownItem>
      <DropdownSeparator />
      <PermissionSimpleDropdownItem onSelect={props.onEditWorkspaces}>
        <EditSidebarCopy />
      </PermissionSimpleDropdownItem>
    </SimpleDropdown>
  );
}
